import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import { CloseIcon } from './Styles/Icons';
import { InputGroup, TextField, ButtonGroup, Button, FlexRow, Select, Dialog, DialogActions, DialogTitle, DialogContent, CircularButton, HighlightBox, HelperText, SectionHint, BoldLabel, LocalizationProvider, DatePicker } from './Styles/UpdateShippingDialogBox';

import { generateShippingUrl } from '../../commons/utils';

import { SHIPPING_COMPANIES, SHIPPING_COMPANY_LABELS } from '../../commons/utils';
import { MenuItem } from '../Styles';
import { looksLikeUrl, isAllowedTrackingUrl } from './Utils';

const COURIER_NAME_MAX_LENGTH = 50;

const getMinEstimatedDeliveryDate = () => {
	const date = new Date();
	date.setHours(0, 0, 0, 0);
	date.setDate(date.getDate() + 1);
	return date;
};

const UpdateShippingDialogBox = ({ open, onTrue, onFalse, shippingCompanyOptions, shippingDetailsMandatory }) => {
	const [error, setError] = useState(null);
	const [shippingCompany, setShippingCompany] = useState('');
	const [shippingId, setShippingId] = useState('');
	const [courierName, setCourierName] = useState('');
	const [trackingIdOrLink, setTrackingIdOrLink] = useState('');
	const [estimatedDelivery, setEstimatedDelivery] = useState(null);
	const [datePickerOpen, setDatePickerOpen] = useState(false);

	const isOtherCourier = shippingCompany === 'OTHER';
	const minEstimatedDeliveryDate = getMinEstimatedDeliveryDate();

	const onTrueHandler = () => {
		let errorState = {};
		setError(errorState);

		const trimmedTrackingValue = trackingIdOrLink.trim();
		const trimmedCourierName = courierName.trim();

		if (shippingDetailsMandatory || shippingCompany || shippingId || courierName) {
			if (!shippingCompany) {
				errorState['shippingCompany'] = 'Shipping company cannot be empty.';
			}
			if (isOtherCourier) {
				if (!trimmedCourierName) {
					errorState['courierName'] = 'Courier name cannot be empty.';
				} else if (trimmedCourierName.length > COURIER_NAME_MAX_LENGTH) {
					errorState['courierName'] = `Courier name cannot exceed ${COURIER_NAME_MAX_LENGTH} characters.`;
				}

				if (trimmedTrackingValue && looksLikeUrl(trimmedTrackingValue) && !isAllowedTrackingUrl(trimmedTrackingValue)) {
					errorState['trackingIdOrLink'] = 'Tracking links must start with http:// or https://.';
				} else if (!trimmedTrackingValue && !estimatedDelivery) {
					errorState['trackingOrDelivery'] = 'Add a tracking ID or link, or an estimated delivery date, so the buyer has something to go on.';
				} else if (estimatedDelivery && estimatedDelivery < minEstimatedDeliveryDate) {
					errorState['estimatedDelivery'] = 'Estimated delivery date must be a future date.';
				}
			} else if (!shippingId) {
				errorState['shippingId'] = 'Shipping ID cannot be empty.';
			}
		}

		if (Object.keys(errorState).length) {
			setError(errorState);
		} else if (isOtherCourier) {
			onTrue({
				isCustomCourier: true,
				courierName: trimmedCourierName,
				value: trimmedTrackingValue,
				estimatedDelivery,
			});
		} else {
			onTrue(shippingCompany && shippingId ? generateShippingUrl(shippingCompany, shippingId) : '');
		}
	};

	const onFalseHandler = () => {
		onFalse();
	};

	useEffect(() => {
		setShippingCompany('');
		setShippingId('');
		setCourierName('');
		setTrackingIdOrLink('');
		setEstimatedDelivery(null);
		setDatePickerOpen(false);
		setError({});
	}, [open]);

	return (
		<Dialog
			open={open}
			onClose={() => onFalseHandler()}
			fullWidth
		>
			<DialogTitle>
				Update Shipping Details
				<ButtonGroup alignRight>
					<CircularButton
						error
						outline
						onClick={() => onFalseHandler()}
					>
						<CloseIcon />
					</CircularButton>
				</ButtonGroup>
			</DialogTitle>

			<DialogContent>
				<FlexRow>
					<InputGroup
						label='Shipping Company'
						markRequired={shippingDetailsMandatory}
						error={error && error.shippingCompany}
					>
						<Select
							name={'shipping_company'}
							placeholder='Select company'
							disabled={false}
							enableSearch={true}
							value={shippingCompany}
							onChange={(e) => {
								setShippingCompany(e.target.value);
								setShippingId('');
								setCourierName('');
								setTrackingIdOrLink('');
								setEstimatedDelivery(null);
								setDatePickerOpen(false);
								setError({});
							}}
							error={error && error.shippingCompany}
						>
							{shippingCompanyOptions.map((item) => (
								<MenuItem
									key={item.value}
									value={item.value}
									disabled={item.disabled}
								>
									{item.name}
								</MenuItem>
							))}
						</Select>
					</InputGroup>
				</FlexRow>

				{isOtherCourier ? (
					<HighlightBox>
						<FlexRow>
							<InputGroup
								label={<BoldLabel>Courier Name</BoldLabel>}
								markRequired={shippingDetailsMandatory}
								error={error && error.courierName}
							>
								<TextField
									name='courier_name'
									placeholder='e.g. RUSH HOUR, Uber, local driver'
									value={courierName}
									disabled={false}
									onChange={(e) => setCourierName(e.target.value)}
									error={error && error.courierName}
									maxLength={COURIER_NAME_MAX_LENGTH}
								/>
							</InputGroup>
						</FlexRow>
						<HelperText>Shown to the buyer in place of a Courier Name.</HelperText>

						<SectionHint>Add at least one of the following, so the buyer knows what to expect.</SectionHint>

						<FlexRow>
							<InputGroup
								label={<BoldLabel>Tracking ID or Link</BoldLabel>}
								error={error && (error.trackingIdOrLink || error.trackingOrDelivery)}
							>
								<TextField
									name='tracking_id_or_link'
									placeholder='Reference no. or https://...'
									value={trackingIdOrLink}
									disabled={false}
									onChange={(e) => setTrackingIdOrLink(e.target.value)}
									error={error && (error.trackingIdOrLink || error.trackingOrDelivery)}
								/>
							</InputGroup>

							<InputGroup
								label={<BoldLabel>Estimated Delivery</BoldLabel>}
								error={error && error.estimatedDelivery}
							>
								<LocalizationProvider>
									<DatePicker
										value={estimatedDelivery}
										open={datePickerOpen}
										disableCloseOnSelect={false}
										inputFormat='MM/dd/yyyy'
										minDate={minEstimatedDeliveryDate}
										onChange={(date) => setEstimatedDelivery(date)}
										onClose={() => setDatePickerOpen(false)}
										renderInput={(params) => (
											<TextField
												ref={params.ref}
												inputRef={params.inputRef}
												value={params.value}
												onClick={() => setDatePickerOpen(true)}
												inputProps={{ ...params.inputProps, readOnly: true }}
												error={error && error.estimatedDelivery}
											/>
										)}
										PopperProps={{ placement: 'bottom-start', style: { zIndex: 1400 } }}
									/>
								</LocalizationProvider>
							</InputGroup>
						</FlexRow>
					</HighlightBox>
				) : (
					<FlexRow>
						<InputGroup
							label='Shipping ID'
							markRequired={shippingDetailsMandatory}
							error={error && error.shippingId}
						>
							<TextField
								name='shipping_id'
								placeholder='Enter shipping id'
								value={shippingId}
								disabled={false}
								onChange={(e) => setShippingId(e.target.value)}
								error={error && error.shippingId}
							/>
						</InputGroup>
					</FlexRow>
				)}
			</DialogContent>

			<DialogActions style={{ margin: '0 0.5rem' }}>
				<ButtonGroup alignRight>
					<Button
						primary
						outline
						onClick={(e) => onFalseHandler()}
					>
						Cancel
					</Button>
					<Button
						primary
						onClick={(e) => onTrueHandler()}
					>
						Confirm
					</Button>
				</ButtonGroup>
			</DialogActions>
		</Dialog>
	);
};

const mapStatesToProps = (state) => {
	const shippingCompanyOptions = Object.keys(SHIPPING_COMPANIES).map((company) => ({ name: SHIPPING_COMPANY_LABELS[company] || company, value: company, disabled: false }));
	
	return {
		supplier: state.supplier.supplier,
		shippingCompanyOptions,
		shippingDetailsMandatory: state?.supplier?.sellerPortalConfig?.orders?.shipping_details_mandatory !== false,
	};
};

const mapFunctionsToProps = {};

export default connect(mapStatesToProps, mapFunctionsToProps)(UpdateShippingDialogBox);
